import React from 'react'
import PropTypes from 'prop-types'
import DefaultLayout from 'js/components/DefaultLayout'
import withSSR from 'js/components/withSSR/withSSR'
import classNames from 'classnames'
import Spinner from 'js/components/Spinner/Spinner'
import ErrorPage from 'js/pages/ErrorPage'
import PageBlock from './PageBlock'
import { useInView } from 'react-intersection-observer'
import loadable from '@loadable/component'
import DatoPageHeader from 'js/pages/DatoPage/DatoPageHeader'
import { fallbackValues } from 'js/utils/fallbackValues'
import { runQuery, getDatoLocale } from 'js/dato'
import { query } from './query'
import { OnPageNav, OnPageNavSection } from '../FeaturesPage/OnPageNav'

const Footer = loadable(() => import('js/components/Footer/Footer'))

function DatoPage({ data, isLoading, error }) {
  const [belowFoldRef, scrolledBelowFold] = useInView({ triggerOnce: true })
  if (isLoading)
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner width="50" color="green" />
      </div>
    )

  if (error) {
    return <ErrorPage error={error} />
  }

  const pageData = fallbackValues(data.page, data.fallback)

  const headerCta = pageData.headerCta[0]

  const Wrapper = pageData.onPageNavigation ? OnPageNav : 'div'
  const SectionWrapper = pageData.onPageNavigation ? OnPageNavSection : 'div'

  return (
    <DefaultLayout showInstallBanner={pageData.showMobileBanner}>
      <div className="bg-BgSub">
        <DatoPageHeader
          heading={pageData.heading}
          preamble={pageData.preamble}
          screenshot={pageData.headerImage && pageData.headerImage.url}
          firstSectionBackground={
            pageData.sections[0] &&
            pageData.sections[0].background === 'secondary'
              ? 'bg-BgSub'
              : 'bg-Bg'
          }
          backgroundImage={pageData.mainImage}
          overlayGradient={pageData.imageGradient}
          showButtons={false} // This will be used later
          showHeader={pageData.showMenu}
          nativeAd={false}
          headerCta={headerCta}
          partnerLogo={pageData.partnerLogo}
        />
        <div id="start" className="bg-Bg">
          <Wrapper>
            {pageData.sections.map((section, index) => {
              return (
                <SectionWrapper
                  key={section.id}
                  heading={section.heading}
                  id={section.heading.toLowerCase().split(' ').join('-')}
                >
                  <div
                    ref={index === pageData.sections.length - 1 && belowFoldRef}
                    className={classNames(
                      section.background === 'secondary' ? 'bg-BgSub' : 'bg-Bg',
                      'py-40',
                    )}
                  >
                    {section.heading && (
                      <h2 className="type-large mx-auto mb-24 max-w-968 px-12 text-center leading-default last:mb-0 md:mb-32 md:px-24">
                        {section.heading}
                      </h2>
                    )}
                    {section.sectionBlocks.map(
                      ({ id, __typename: type, ...props }) => {
                        return (
                          <PageBlock
                            key={id}
                            type={type}
                            {...props}
                            width={section.width}
                          />
                        )
                      },
                    )}
                  </div>
                </SectionWrapper>
              )
            })}
          </Wrapper>
        </div>
        {pageData.showMenu && scrolledBelowFold && <Footer marginless />}
      </div>
    </DefaultLayout>
  )
}

DatoPage.propTypes = {
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
}

DatoPage.getInitialProps = ({ params, location, language, isPreview }) => {
  const locale = getDatoLocale(language)
  const slug =
    params.slug ||
    location.pathname.replace(/^\/\w{2}\//g, '').replace(/\//g, '')

  return runQuery(query, { slug, locale }, isPreview).then(
    ({ page, fallback }) => {
      if (page === null && fallback === null) {
        return
      }

      return { page, fallback }
    },
  )
}

export default withSSR(DatoPage)
